import { zodResolver } from "@hookform/resolvers/zod";
import { WarningIcon } from "@phosphor-icons/react";
import clsx from "clsx";
import { useId, useMemo } from "react";
import { useForm, Controller } from "react-hook-form";
import { useIntl, type IntlShape } from "react-intl";
import { z } from "zod";

import { Alert } from "@/components/Alert/Alert";
import { Button } from "@/components/Button";
import { IconCheck, IconPromoCode } from "@/components/Icons";
import { TextField } from "@/components/TextField/TextField";
import type { ValidationStatus } from "@/hooks/usePromoCodeValidation";

const createValidationSchema = (intl: IntlShape) =>
  z.object({
    code: z
      .string()
      .trim()
      .min(
        1,
        intl.formatMessage({
          defaultMessage: "Promo code is required",
          description:
            "Validation error shown when the promo code field is submitted empty",
        }),
      ),
  });

type PromoCodeFormValues = z.infer<ReturnType<typeof createValidationSchema>>;

export type Props = {
  appliedCode?: string;
  validationStatus?: ValidationStatus;
  onCodeApply: (values: PromoCodeFormValues) => void;
  onCodeRemove: (data: { code: string; isApplied: boolean }) => void;
  onFocus?: (hasAppliedPromoCode: boolean) => void;
};

export function PromoCodeForm({
  appliedCode,
  validationStatus,
  onCodeApply,
  onCodeRemove,
  onFocus,
}: Props) {
  const intl = useIntl();
  const headerId = useId();

  const promoCodeFormSchema = useMemo(
    () => createValidationSchema(intl),
    [intl],
  );

  const {
    handleSubmit,
    formState: { isValid },
    reset,
    control,
  } = useForm<
    z.input<typeof promoCodeFormSchema>,
    undefined,
    PromoCodeFormValues
  >({
    resolver: zodResolver(promoCodeFormSchema),
    defaultValues: {
      code: appliedCode || "",
    },
  });

  const isLoading = validationStatus?.type === "loading";

  return (
    <div className="section-card">
      <div
        id={headerId}
        className="mb-4 flex items-center gap-3 text-lg font-medium text-text-primary"
      >
        <IconPromoCode className="size-10 shrink-0 rounded-lg bg-surface-accent-soft p-2 text-surface-accent-strong" />
        {intl.formatMessage({
          defaultMessage: "Add promo code",
          description: "Label or heading for the promo code entry section",
        })}
      </div>

      <form onSubmit={handleSubmit(onCodeApply)}>
        <Controller
          name="code"
          control={control}
          render={({ field, fieldState }) => (
            <TextField
              {...field}
              fullWidth
              inputProps={{ "aria-labelledby": headerId }}
              inputLabel={{ hidden: true }}
              onFocus={() => {
                onFocus?.(!!appliedCode);
              }}
              placeholder={intl.formatMessage({
                defaultMessage: "Enter code",
                description: "Placeholder text for the promo code input field",
              })}
              disabled={isLoading}
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
              readOnly={!!appliedCode}
              endAdornment={
                <div className="-me-4 shrink-0">
                  {appliedCode && !isLoading ? (
                    <Button
                      key="remove-button"
                      type="button"
                      variant="text"
                      color="secondary"
                      onClick={() => {
                        onCodeRemove({
                          code: appliedCode,
                          isApplied: validationStatus?.type === "success",
                        });
                        reset({ code: "" });
                      }}
                      size="small"
                    >
                      {intl.formatMessage({
                        defaultMessage: "Remove",
                        description:
                          "Generic 'Remove' button label to delete or deselect an item",
                      })}
                    </Button>
                  ) : (
                    <Button
                      key="apply-button"
                      type="submit"
                      variant="text"
                      disabled={!isValid}
                      loading={isLoading}
                      size="small"
                    >
                      {intl.formatMessage({
                        defaultMessage: "Apply",
                        description:
                          "Button to apply the entered promo code to the current booking or price",
                      })}
                    </Button>
                  )}
                </div>
              }
            />
          )}
        />

        {validationStatus?.type === "success" ||
        validationStatus?.type === "error" ? (
          <div className="mt-3">
            <Alert
              severity={validationStatus.type}
              icon={
                validationStatus.type === "success" ? (
                  <IconCheck className="size-8 animate-pop-in" />
                ) : (
                  <WarningIcon className="size-6" weight="bold" />
                )
              }
            >
              <span
                className={clsx("items-center", {
                  "font-bold text-core-accent-500":
                    validationStatus.type === "success",
                  "font-medium": validationStatus.type !== "success",
                })}
              >
                {validationStatus.message}
              </span>
            </Alert>
          </div>
        ) : null}
      </form>
    </div>
  );
}
